import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Network, Users, Target, Zap, DollarSign } from 'lucide-react';
import { useTheme } from '../../../context/ThemeContext';

const NetworkVisualization = () => {
  const { isDarkMode } = useTheme();
  const [selectedLevel, setSelectedLevel] = useState(0);

  const networkData = {
    levels: [
      { level: 1, users: 5, commission: 1500, growth: 12 },
      { level: 2, users: 25, commission: 4500, growth: 8 },
      { level: 3, users: 125, commission: 12500, growth: 15 },
      { level: 4, users: 625, commission: 31250, growth: 22 },
      { level: 5, users: 3125, commission: 78125, growth: 18 }
    ]
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm rounded-3xl p-6 border ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-sm`}
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Visualización de Red</h3>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Estructura completa de tu organización</p>
        </div>
        <div className="flex items-center space-x-2">
          <Network className="w-5 h-5 text-blue-500" />
          <span className="text-sm font-medium text-gray-700">5 Niveles</span>
        </div>
      </div>

      {/* Niveles de la red */}
      <div className="space-y-4">
        {networkData.levels.map((level, index) => (
          <motion.div
            key={level.level}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 + index * 0.1 }}
            className={`p-4 rounded-2xl border-2 transition-all duration-300 cursor-pointer ${
              selectedLevel === level.level
                ? `border-orange-500 ${isDarkMode ? 'bg-orange-900/50' : 'bg-orange-50/50'} shadow-md`
                : `${isDarkMode ? 'border-gray-600/50 hover:border-gray-500' : 'border-gray-200/50 hover:border-gray-300'}`
            }`}
            onClick={() => setSelectedLevel(level.level)}
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                  selectedLevel === level.level
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600'
                    : `${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`
                }`}>
                  <Users className={`w-6 h-6 ${
                    selectedLevel === level.level ? 'text-white' : `${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`
                  }`} />
                </div>
                <div>
                  <h4 className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                    Nivel {level.level}
                  </h4>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {level.users.toLocaleString()} miembros
                  </p>
                </div>
              </div>

              <div className="text-right">
                <div className="flex items-center space-x-2 mb-1">
                  <DollarSign className="w-4 h-4 text-green-500" />
                   <span className={`font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                     ${level.commission.toLocaleString()}
                   </span>
                </div>
                <div className="flex items-center space-x-1">
                  <Target className="w-3 h-3 text-orange-500" />
                   <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                     +{level.growth}% crecimiento
                   </span>
                </div>
              </div>
            </div>

            {/* Barra de progreso */}
            <div className="mt-3">
              <div className={`flex justify-between text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>
                <span>Progreso del nivel</span>
                <span>{Math.min(100, (level.users / 5000) * 100).toFixed(1)}%</span>
              </div>
              <div className={`w-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-full h-2`}>
                <motion.div
                  className="h-2 rounded-full bg-gradient-to-r from-orange-500 to-orange-600"
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(100, (level.users / 5000) * 100)}%` }}
                  transition={{ duration: 1, delay: 0.7 + index * 0.1 }}
                />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Resumen de la red */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
        className="mt-6 p-4 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl text-white"
      >
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-semibold">Resumen de la Red</h4>
            <p className="text-sm opacity-90">
              {networkData.levels.reduce((sum, level) => sum + level.users, 0).toLocaleString()} miembros totales
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">
              ${networkData.levels.reduce((sum, level) => sum + level.commission, 0).toLocaleString()}
            </div>
            <div className="text-sm opacity-90">Comisiones totales</div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default NetworkVisualization;